import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box } from "monday-ui-react-core";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useCurrentMondayContext } from "@/components/AppContext";
import { CommandEditor } from "@/components/CommandEditor";
import { BorderedBox } from "@/components/Common";
import { useServiceContext } from "@/services";
import { PageName } from "@/services/analytics";
import type { PollResult, RunResult } from "@/services/runcli";
import { ExitCode } from "@/services/runcli";

import { ButtonBar } from "./ButtonBar";
import styles from "./CliRunner.module.scss";
import { ResultBox } from "./ResultBox";

interface PollArgs {
  requestId: string;
}

interface Results {
  success: boolean;
  output?: string;
  duration?: number;
}

type Props = {
  initialCommand: string;
};

export function CliRunner({ initialCommand }: Props) {
  const { analytics, monday, cli } = useServiceContext();
  const [commandText, setCommandText] = useState(initialCommand);
  const queryClient = useQueryClient();
  const mondayContext = useCurrentMondayContext();

  const {
    data: requestId,
    isFetching: isFetchingCommandId,
    refetch: doRunCommand,
    error: runError,
  } = useQuery<RunResult, Error>({
    enabled: false,
    retry: false,
    queryKey: ["runCommand"],
    queryFn: async () => {
      const requestId = crypto.randomUUID();
      await cli.runCommand(commandText, requestId, mondayContext, monday);
      return requestId;
    },
  });

  const {
    data: commandResult,
    isFetching: isFetchingCommandResult,
    error: pollError,
  } = useQuery<PollResult | undefined, Error>({
    // We will retry every 'retryDelay' ms at most 'retry' times until result.status is not PENDING
    retry: 150, // retry every two seconds for 5 minutes
    retryDelay: 2000, // two seconds
    enabled: !!requestId,
    queryKey: ["pollCommand", { requestId: requestId }],
    queryFn: async () => {
      if (!requestId) {
        // If this happens, it's a logic error since the query is only enabled when there is a requestId
        console.error("Request ID expected.");
        return;
      }
      const sessionToken = await monday.fetchSessionToken();
      const r = await cli.pollCommand(requestId, mondayContext, sessionToken);
      if (r.retry) {
        throw new Error("Results pending...");
      }
      await analytics.sendCommandExecuted(r.exitCode === ExitCode.SUCCESS);
      return r;
    },
  });

  useEffect(() => {
    queryClient.removeQueries({
      predicate: (query) => {
        if (requestId) {
          const args = query.queryKey[1] as PollArgs;
          return query.queryKey[0] === "pollCommand" && args?.requestId !== requestId;
        }
        return false;
      },
    });
  }, [queryClient, requestId]);

  useEffect(() => {
    analytics.sendPageViewedEvent(PageName.Runner).catch((e) => {
      console.error(e);
    });
  }, [analytics]);

  useEffect(() => {
    // Required when updating the state by copying an example
    setCommandText(initialCommand);
  }, [initialCommand]);

  const runCommand = useCallback(() => {
    doRunCommand().catch((e) => {
      // TODO: proper error handling
      console.error(e);
    });
  }, [doRunCommand]);

  const results = useMemo((): Results | undefined => {
    if (isFetchingCommandResult) {
      return;
    }

    if (runError) {
      console.error(runError);
      const message = `Unable to submit command: ${formatErrorMessage(runError)}`;
      return { success: false, output: message };
    }
    if (pollError) {
      console.error(pollError);
      const message = `Unable to retrieve results: ${formatErrorMessage(pollError)}`;
      return { success: false, output: message };
    }
    if (!commandResult) {
      // Not sure what to do in this case. This *should* be covered by the pollError checking above.
      return;
    }
    if (!commandResult.results || commandResult.results.length === 0) {
      console.error(commandResult);
      const message = `Unexpected results.`;
      return { success: false, output: message };
    }

    return {
      success: commandResult.exitCode === ExitCode.SUCCESS,
      output: commandResult.results[0].output,
      duration: commandResult.duration,
    };
  }, [isFetchingCommandResult, runError, pollError, commandResult]);

  const loading = isFetchingCommandId || isFetchingCommandResult;

  return (
    <Box className={styles.cliRunnerContainer}>
      <BorderedBox
        borderColor={Box.borderColors.UI_BORDER_COLOR}
        paddingX={Box.paddingXs.XS}
        marginBottom={Box.marginBottoms.SMALL}
      >
        <CommandEditor
          disabled={loading}
          placeholder="The command to run, e.g. '--action getWorkspaceList --bestView'"
          value={commandText}
          onChange={setCommandText}
        />
      </BorderedBox>
      <Box marginTop={Box.marginTops.LARGE}>
        <ButtonBar
          onRun={runCommand}
          onClear={() => setCommandText("")}
          runDisabled={!commandText || commandText.trim().length === 0}
          clearDisabled={!commandText}
          loading={loading}
        />
        <>{results && <ResultBox success={results.success} results={results.output} duration={results.duration} />}</>
      </Box>
    </Box>
  );
}

// TODO: Move this to a better place & share with API code
function formatErrorMessage(err: Error): string {
  let message: string;
  if (err.stack) {
    // Lambda errors seem to contain the name & message in the stack value
    message = err.stack;
  } else {
    message = `${err.name}: ${err.message}`;
  }
  return message;
}
